import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useApplication } from '../../../hooks/useApplication';
import {
  AcknowledgementDetailsResponse,
  FeeSplitFeeTypeEnum,
  MemberFeeSplit,
  TeamControllerApi,
} from '../../../openapi/yenta';
import ErrorService from '../../../services/ErrorService';
import { showErrorToast } from '../../../slices/ToastNotificationSlice';
import { useAppDispatch, useAppSelector } from '../../../slices/store';
import Logger from '../../../utils/Logger';
import { getYentaConfiguration } from '../../../utils/OpenapiConfigurationUtils';
import DefaultLoader from '../../DefaultLoader';
import TeamAgreement from './TeamAgreement';

interface TeamCompensationFieldProps {
  name: string;
}

/**
 * Leader information in the compensation plan
 */
interface TeamCompensationLeader {
  leaderName: string;
  leaderSplitPercent: number;
  paysRealSplitPercent: number | null;
  paysCbrFeePercent: number | null;
  paysBrokerageFeePercent: number | null;
  paysPostCapTransactionFeePercent: number | null;
}

/**
 * Individual fee percentages
 */
interface TeamCompensationIndividualFees {
  splitPercent: number;
  cbrPercent: number;
  brokeragePercent: number;
  postCapTransactionPercent: number;
}

/**
 * Team fees structure
 */
interface TeamCompensationTeamFees {
  capAmount: number | undefined;
  preCapFee: number | undefined;
  postCapFee: number | undefined;
}

/**
 * Compensation plan data structure
 */
interface TeamCompensationPlanData {
  capLevel: number;
  individualFees: TeamCompensationIndividualFees;
  leaders: TeamCompensationLeader[];
  teamFees: TeamCompensationTeamFees;
}

/**
 * Team Compensation Plan Attestation Answer
 * This is the answer payload that gets submitted to the API
 */
export interface TeamCompensationPlanAttestationAnswer {
  attestationType: 'TeamCompensationPlanAttestation';
  entityId: string;
  entityVersion?: number;
  attestedBy: string;
  compensationPlanData: TeamCompensationPlanData;
}

/**
 * Calculate individual fee percentage (100% - sum of all leader fees for that type)
 */
const calculateIndividualFee = (
  memberFeeSplits: MemberFeeSplit[] | undefined,
  feeType: string,
): number => {
  if (!memberFeeSplits) return 100;

  let totalLeaderFee = 0;
  memberFeeSplits.forEach((split) => {
    const fee = split.feeSplits?.find((f) => f.feeType === feeType);
    if (fee) {
      totalLeaderFee += fee.percent || 0;
    }
  });

  return Math.max(0, 100 - totalLeaderFee);
};

/**
 * TeamCompensationField component that fetches and displays team compensation data
 * Used in ConfigDriven forms for Team Compensation Plan Attestation fields
 */
const TeamCompensationField: React.FC<TeamCompensationFieldProps> = ({
  name,
}) => {
  const dispatch = useAppDispatch();
  const { setValue } = useFormContext();
  const [teamInvitationData, setTeamInvitationData] =
    useState<AcknowledgementDetailsResponse | null>(null);
  const [invitationId, setInvitationId] = useState<string | null>(null);
  const [isLoadingTeamData, setIsLoadingTeamData] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const { authUserDetail } = useAppSelector((state) => state.auth);

  const legacyApplicationId = authUserDetail?.onboardings?.find(
    (item) => item.legacyApplicationId,
  )?.legacyApplicationId;

  // Use the useApplication hook to fetch application data
  const {
    data: applicationData,
    isLoading: isLoadingApplication,
    error: applicationError,
  } = useApplication(legacyApplicationId);

  // Fetch team invitation details when application data is available
  useEffect(() => {
    const fetchTeamInvitationDetails = async () => {
      if (!applicationData?.teamInvitation) return;

      try {
        setIsLoadingTeamData(true);
        setTeamError(null);

        const teamInvitation = applicationData.teamInvitation;

        const fetchedInvitationId =
          teamInvitation.invitationId || teamInvitation.genericTeamInvitation;

        if (!fetchedInvitationId) {
          throw new Error('No invitation ID provided');
        }

        setInvitationId(fetchedInvitationId);

        const { data } = await new TeamControllerApi(
          await getYentaConfiguration(),
        ).getTeamInvitationAcknowledgmentDetails(fetchedInvitationId);

        setTeamInvitationData(data);
      } catch (err) {
        Logger.error('Failed to fetch team compensation details:', err);
        ErrorService.notify(
          'Failed to fetch team compensation details',
          err as Error,
        );
        dispatch(
          showErrorToast(
            'Unable to load team compensation details. Please try again.',
          ),
        );
        setTeamError('Failed to load team compensation details');
      } finally {
        setIsLoadingTeamData(false);
      }
    };

    fetchTeamInvitationDetails();
  }, [applicationData, dispatch]);

  // Set the attestation answer when data is loaded
  useEffect(() => {
    if (teamInvitationData && invitationId && authUserDetail?.id) {
      // Transform API data to match the expected payload format
      const compensationPlanData: TeamCompensationPlanData = {
        capLevel: teamInvitationData.capLevel || 0,
        individualFees: {
          splitPercent:
            calculateIndividualFee(
              teamInvitationData.memberFeeSplits,
              FeeSplitFeeTypeEnum.RealSplit,
            ) / 100,
          cbrPercent:
            calculateIndividualFee(
              teamInvitationData.memberFeeSplits,
              FeeSplitFeeTypeEnum.CbrFee,
            ) / 100,
          brokeragePercent:
            calculateIndividualFee(
              teamInvitationData.memberFeeSplits,
              FeeSplitFeeTypeEnum.BrokerageFee,
            ) / 100,
          postCapTransactionPercent:
            calculateIndividualFee(
              teamInvitationData.memberFeeSplits,
              FeeSplitFeeTypeEnum.TransactionFee,
            ) / 100,
        },
        leaders:
          teamInvitationData.leaderSplits?.map((split) => {
            // Use fullName from API response directly
            const leaderName = split.fullName || 'Unknown Leader';

            // Find the fee splits for this leader
            const leaderFeeSplits = teamInvitationData.memberFeeSplits?.find(
              (feeSplit) => feeSplit.leaderId === split.leaderId,
            );

            // Helper to get fee percent or null
            const getFeePercent = (feeType: string): number | null => {
              const fee = leaderFeeSplits?.feeSplits?.find(
                (f) => f.feeType === feeType,
              );
              return fee?.percent !== undefined ? fee.percent / 100 : null;
            };

            return {
              leaderName,
              leaderSplitPercent: (split.leaderSplit || 0) / 100,
              paysRealSplitPercent: getFeePercent(
                FeeSplitFeeTypeEnum.RealSplit,
              ),
              paysCbrFeePercent: getFeePercent(FeeSplitFeeTypeEnum.CbrFee),
              paysBrokerageFeePercent: getFeePercent(
                FeeSplitFeeTypeEnum.BrokerageFee,
              ),
              paysPostCapTransactionFeePercent: getFeePercent(
                FeeSplitFeeTypeEnum.TransactionFee,
              ),
            };
          }) || [],
        teamFees: {
          capAmount: teamInvitationData.teamFeeCap?.capAmount,
          preCapFee: teamInvitationData.teamFeeCap?.preCapFee,
          postCapFee: teamInvitationData.teamFeeCap?.postCapFee,
        },
      };

      const answer = {
        attestationType: 'TeamCompensationPlanAttestation',
        entityId: invitationId,
        attestedBy: authUserDetail.id,
        compensationPlanData,
      } satisfies TeamCompensationPlanAttestationAnswer;

      setValue(name, answer);
    }
  }, [teamInvitationData, invitationId, authUserDetail?.id, name, setValue]);

  const isLoading = isLoadingApplication || isLoadingTeamData;
  const error = applicationError || teamError;

  if (isLoading) {
    return (
      <div className='h-64 w-full'>
        <DefaultLoader />
      </div>
    );
  }

  if (error || !teamInvitationData) {
    return (
      <div className='w-full p-6 bg-gray-50 border border-gray-200 rounded-lg'>
        <p className='font-inter text-sm text-gray-700'>
          {error || 'No team compensation data available.'}
        </p>
      </div>
    );
  }

  return <TeamAgreement teamInvitationData={teamInvitationData} />;
};

export default TeamCompensationField;
